import {
  CountryCode,
  getCountryCallingCode,
  isPossiblePhoneNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js'
import { includes, isNil, length, split } from 'ramda'
import { times } from '~/constants/config'
import { getRegionConfig } from '~/constants/regions'
import { i18nConfig } from '~/i18nInstance'
import { getApiEnvironment } from '~/utils/common'

import {
  formatDateString,
  formatWeekdayString,
  isDate,
  isToday,
  isWithinLastWeek,
  isYesterday,
} from '@mangovoice/core'

function capitalizeString(str: string): string {
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`
}

function toNounCasing(str: string): string {
  //uppercase the first letter, lowerize all others, split on space
  return str
    .split(' ')
    .map((word) => {
      //special case for abbreviations
      //capitalized abbrevation
      if (word.length < 4 && word.toUpperCase() === word) {
        return word
      }

      //abbreviation with a period already noted
      if (word.length <= 4 && word.endsWith('.')) {
        return word
      }

      //word can be noun-casified
      return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
    })
    .join(' ')
}

const extractDigits = (input: string): string => {
  return input.replace(/\D/g, '')
}

const normalizeRawInput = (input: string) => {
  const trimmed = input.trim()
  const cleaned = extractDigits(trimmed)
  const hasPlusPrefix = trimmed.startsWith('+')
  return { normalizedInput: trimmed, cleaned, hasPlusPrefix }
}

function formatPhoneNumber1(phoneNumberString?: string): string {
  if (!phoneNumberString) return ''

  const cleaned = extractDigits(phoneNumberString)
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`
  }
  return `${phoneNumberString}`
}

function formatPhoneNumber2(phoneNumberString: string): string {
  const match = split(' ', `${phoneNumberString}`)
  if (match.length > 0) {
    let numberString: string
    if (match[2]) {
      numberString = `(${match[0]}) ${match[1]}-${match[2]}`
    } else {
      numberString = `${match[0]}-${match[1]}`
    }
    return numberString
  }
  return `${phoneNumberString}`
}

const phoneFormatter = (phone: string) => {
  if (isNil(phone)) {
    return ''
  }

  if (phone.length > 10) {
    phone = phone.substring(phone.length - 10)
  }

  let phoneNumber: string

  try {
    const number = parsePhoneNumberWithError(phone).formatNational()
    phoneNumber = formatPhoneNumber2(number)
  } catch (_error) {
    phoneNumber = formatPhoneNumber1(phone)
  }
  return phoneNumber
}

const templatePhoneNumber = (phoneNumber: string): string => {
  return `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(
    3,
    6,
  )}-${phoneNumber.substring(6)}`
}

export const getDefaultCountryCode = (): CountryCode => {
  try {
    const environment = getApiEnvironment()
    const regionConfig = getRegionConfig(environment)
    if (regionConfig.abbreviation === 'UK') {
      return 'GB'
    }
    return 'US'
  } catch (_error) {
    return 'US'
  }
}

const formatInternationalUS = (
  number: ReturnType<typeof parsePhoneNumberWithError>,
): string | null => {
  if (
    number.countryCallingCode === '1' ||
    number.country === 'US' ||
    number.country === 'CA'
  ) {
    const nationalNumber = number.nationalNumber
    if (nationalNumber.length === 10) {
      return `+1 ${templatePhoneNumber(nationalNumber)}`
    }
  }
  return null
}

const formatDomesticUS = (
  number: ReturnType<typeof parsePhoneNumberWithError>,
): string | null => {
  const nationalNumber = number.nationalNumber
  if (
    nationalNumber.length === 10 &&
    (number.country === 'US' || number.country === 'CA')
  ) {
    return templatePhoneNumber(nationalNumber)
  }
  return null
}

const formatFallback = (
  cleaned: string,
  hasPlusPrefix: boolean,
  defaultCountry: CountryCode,
): string => {
  if (defaultCountry === 'GB') {
    return hasPlusPrefix ? `+${cleaned}` : cleaned
  }
  if (cleaned.length === 7) {
    return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 7)}`
  }
  if (cleaned.length >= 8 && cleaned.length <= 10) {
    return templatePhoneNumber(cleaned)
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return templatePhoneNumber(cleaned.substring(1))
  }
  if (hasPlusPrefix) {
    return `+${cleaned}`
  }
  return cleaned
}

/**
 * Formats a phone number for display in the UI.
 * Handles international numbers, domestic numbers, and special codes (*, #).
 *
 * @param phoneNumberString - The phone number to format
 * @returns Formatted phone number string for display
 */
export const formatPhoneNumber = (
  phoneNumberString: string | undefined | null,
): string => {
  if (!phoneNumberString || typeof phoneNumberString !== 'string') {
    return ''
  }

  if (
    phoneNumberString.charAt(0) === '#' ||
    phoneNumberString.charAt(0) === '*'
  ) {
    const cleaned = extractDigits(phoneNumberString)
    return `${phoneNumberString.charAt(0)}${cleaned}`
  }

  const { normalizedInput, cleaned, hasPlusPrefix } =
    normalizeRawInput(phoneNumberString)

  if (cleaned.length <= 6 && !hasPlusPrefix) {
    return cleaned
  }

  const defaultCountryForFormatting = getDefaultCountryCode() // default country based on data env

  try {
    const number = parsePhoneNumberWithError(normalizedInput, {
      defaultCountry: defaultCountryForFormatting,
    })

    if (hasPlusPrefix) {
      const usFormat = formatInternationalUS(number)
      if (usFormat) {
        return usFormat
      }
      return number.formatInternational()
    }

    if (number.isValid()) {
      return number.formatNational()
    }

    if (isPossiblePhoneNumber(number.number)) {
      const usFormat = formatDomesticUS(number)
      if (usFormat) {
        return usFormat
      }
      // An incomplete number can lose its trunk prefix (the UK leading 0) in
      // national format. The result is written back into the dial input, so it
      // must keep every digit the user typed.
      const national = number.formatNational()
      if (extractDigits(national) === cleaned) {
        return national
      }
    }
  } catch (_error) {
    // fall through
  }

  return formatFallback(cleaned, hasPlusPrefix, defaultCountryForFormatting)
}

/**
 * Converts a phone number to E.164. A leading `+` or the region's international
 * dialing prefix (00 in GB, 011 in US) is parsed as international; anything
 * else is parsed against the deployment region's default country.
 *
 * @returns The E.164 number, or null when the input isn't a possible phone number
 */
export const toE164 = (
  phoneNumberString: string | undefined | null,
): string | null => {
  if (!phoneNumberString) return null
  const { normalizedInput } = normalizeRawInput(phoneNumberString)
  try {
    const number = parsePhoneNumberWithError(normalizedInput, {
      defaultCountry: getDefaultCountryCode(),
    })
    return number.isPossible() ? number.number : null
  } catch (_error) {
    return null
  }
}

/**
 * Formats a stored (usually E.164) phone number for display. Numbers in the
 * deployment region's calling code show nationally; others show internationally.
 */
export const formatPhoneNumberForRegion = (
  phoneNumberString: string | undefined | null,
): string => {
  if (!phoneNumberString) return ''
  const { normalizedInput, hasPlusPrefix } =
    normalizeRawInput(phoneNumberString)
  if (hasPlusPrefix) {
    try {
      const number = parsePhoneNumberWithError(normalizedInput)
      if (
        number.countryCallingCode ===
        getCountryCallingCode(getDefaultCountryCode())
      ) {
        return formatPhoneNumber(number.formatNational())
      }
    } catch (_error) {
      // fall through
    }
  }
  return formatPhoneNumber(normalizedInput)
}

const getValidNumber = (phone: string | undefined | null) => {
  if (!phone) return ''
  const hasPlusPrefix = phone.trim().startsWith('+')
  const destination = phone.replace(/\D+/g, '')
  if (phone.charAt(0) === '*') {
    return `*${destination}`
  }
  if (
    !hasPlusPrefix &&
    destination.length === 11 &&
    destination.startsWith('1')
  ) {
    return destination.substring(1)
  }
  return hasPlusPrefix ? `+${destination}` : destination
}

/**
 * Formats a date into a localized relative representation.
 *
 * @param dateString - A Date object or string representing a date
 * @returns A localized string:
 *  - "Today" or its localized equivalent if the date is today
 *  - "Yesterday" or its localized equivalent if the date is yesterday
 *  - Weekday name if within the last week
 *  - Formatted date string (e.g., "05/20/23") otherwise
 *  - "Invalid Date" if parsing fails
 *
 * @example
 * formatRelativeDay(new Date()) // Returns "Today" (localized)
 * formatRelativeDay("2023-05-19") // Returns "Yesterday" (localized) if today is 2023-05-20
 */
export const formatRelativeDay = (dateString: string | Date) => {
  try {
    const date = new Date(dateString)
    if (!isDate(date)) {
      console.error('dateFormatter: Invalid date string provided', { date })
      return 'Invalid Date'
    }
    // const date = getValidDate(dateString)

    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (isToday(date)) {
      return i18nConfig.t('today')
    }

    if (isYesterday(date)) {
      return i18nConfig.t('yesterday')
    }

    if (isWithinLastWeek(date)) {
      return formatWeekdayString(date)
    }

    return formatDateString(date)
  } catch (error) {
    console.error('LibError: Failed to format date', { dateString, error })
    return 'Invalid Date'
  }
}

const convertCallDuration = (durationInSeconds: number) => {
  if (durationInSeconds === 0) return '0:00'
  const seconds = durationInSeconds % times.secsOfMin
  const mins = Math.floor(durationInSeconds / times.secsOfMin)
  const displayMins = mins % times.minsOfHour
  const hours = Math.floor(mins / times.minsOfHour)
  if (mins >= times.minsOfHour) {
    return `${hours}:${displayMins < 10 ? '0' : ''}${displayMins}:${
      seconds < 10 ? '0' : ''
    }${seconds}`
  }
  if (mins > 0) {
    return `${mins}:${seconds < 10 ? '0' : ''}${seconds}`
  }
  return `0:${seconds < 10 ? '0' : ''}${seconds}`
}
const convertCallDurationTruncateMilliseconds = (durationInSeconds: number) => {
  let duration = convertCallDuration(durationInSeconds)
  if (duration.includes('.')) {
    const [toSeconds, milliseconds] = duration.split('.')
    const truncactedMilliseconds = milliseconds.substring(0, 3)
    duration = `${toSeconds}.${truncactedMilliseconds}`
  }
  return duration
}
const convertCallDurationNoMilliseconds = (durationInSeconds: number) => {
  let duration = convertCallDuration(durationInSeconds)
  if (duration.includes('.')) {
    duration = duration.split('.')[0]
  }
  return duration
}

const tripLeadingZeroFromTime = (time: string): string => {
  const parts = time.split(':')
  while (parts.length > 2 && parts[0] === '00') {
    parts.shift()
  }
  parts[0] = parts[0].replace(/^0+/, '') || '0'
  return parts.join(':')
}

const convertVoicemalLength = (d: number): string => {
  const seconds = Math.round(d % times.secsOfMin)
  const mins = Math.floor(d / times.secsOfMin)
  const duration = `${mins}:${seconds < 10 ? '0' : ''}${seconds}`
  return duration
}

const convertTypingLabel = (typerData: string[]): string | null => {
  if (length(typerData) === 0) return null
  const typerName =
    length(typerData) > 2
      ? `${length(typerData)} people`
      : length(typerData) > 1
        ? `${typerData[0]}, ${typerData[1]}`
        : `${typerData[0]}`
  return `${typerName} ${length(typerData) > 1 ? 'are' : 'is'} typing...`
}

const validateEmail = (email: string): boolean => {
  if (includes('@', email)) {
    const re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ // eslint-disable-line no-useless-escape
    return re.test(String(email).toLowerCase())
  }
  return true
}

const cleanInvalidString = (input: string) => {
  let output = ''
  for (let i = 0; i < input.length; i += 1) {
    if (input.charCodeAt(i) <= 127) {
      output += input.charAt(i)
    }
  }
  return output
}

const fileToBase64 = (
  file: File | null,
): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve(null)
    } else {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    }
  })

const dataURLtoFile = (
  dataurl: string | File | null,
  filename: string,
): Promise<File | null> =>
  new Promise((resolve) => {
    if (!dataurl) {
      resolve(null)
    } else if (
      typeof dataurl !== 'string' &&
      dataurl.name &&
      typeof dataurl.name === 'string'
    ) {
      resolve(dataurl)
    } else if (typeof dataurl === 'string') {
      const arr = dataurl.split(','),
        mime = arr[0].match(/:(.*?);/)?.[1] ?? 'application/octet-stream',
        bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      const file = new File([u8arr], filename, { type: mime })
      resolve(file)
    } else {
      resolve(null)
    }
  })

const convertFileSize = (size: number): string => {
  const filesize = Math.round(size / 1024)
  return `${filesize} KB`
}

const getNormalizedName = (name: string): string => {
  if (!name) return name
  const words = name.split(' ')
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const getFormattedTimeDigits = (digits: number): string => {
  return `${digits < 10 ? `0` : ``}${digits}`
}

const formatSeconds = (seconds: number) => {
  const hours = (seconds / 3600) | 0
  let remainingSeconds = seconds - hours * 3600
  const minutes = (remainingSeconds / 60) | 0
  remainingSeconds = (remainingSeconds - minutes * 60) | 0
  if (hours > 0) {
    return `${getFormattedTimeDigits(hours)}:${getFormattedTimeDigits(minutes)}:${getFormattedTimeDigits(
      remainingSeconds,
    )}`
  } else {
    return `${getFormattedTimeDigits(minutes)}:${getFormattedTimeDigits(
      remainingSeconds,
    )}`
  }
}

const formatMilliseconds = (
  milliseconds: number | string | undefined,
): string => {
  if (milliseconds === undefined) return '00:00'
  const ms =
    typeof milliseconds === 'string' ? parseFloat(milliseconds) : milliseconds
  const seconds = ms / 1000
  return formatSeconds(seconds)
}

const getFormattedSnippetShortcut = (value: string) => {
  //only letters, numbers and dashes
  const regex = /[^a-zA-Z0-9-]*$/
  return value.replace(regex, '')
}

/**
 * Formats seconds into a readable duration string (e.g., "2m 30s", "1h 15m", "45s")
 *
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 *
 * @example
 * formatDuration(45) // "45s"
 * formatDuration(150) // "2m 30s"
 * formatDuration(3900) // "1h 5m"
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`
  }
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (remainingMinutes > 0) {
    return `${hours}h ${remainingMinutes}m`
  }
  return `${hours}h`
}

export {
  phoneFormatter,
  getValidNumber,
  cleanInvalidString,
  convertCallDuration,
  convertCallDurationTruncateMilliseconds,
  convertCallDurationNoMilliseconds,
  convertVoicemalLength,
  convertTypingLabel,
  convertFileSize,
  dataURLtoFile,
  fileToBase64,
  validateEmail,
  getNormalizedName,
  getFormattedTimeDigits,
  getFormattedSnippetShortcut,
  formatMilliseconds,
  formatSeconds,
  capitalizeString,
  toNounCasing,
  tripLeadingZeroFromTime,
}
